import { useMe } from "@nu-assist/shared-fe";
import { useAdvisoryFetch, type FetchLike } from "../../context/AdvisoryClientContext";
import { NUADVISORY_BASE } from "../../lib/advisory";
import { Button } from "@nu-assist/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
/**
 * /operations/sync-phone — "Sync Contacts to Phone". Migrated from nuadvisory
 * `Synchronization.cshtml` + `synchronizationPage.js` (Bootstrap tabs + jQuery).
 *
 * Content-only page: the authenticated shell (header/sidebar/footer) is
 * provided by `WebShellLayout` via `<Outlet>`, so this component returns ONLY
 * its page content — no shell wrapper (double-shell otherwise).
 *
 * Frontend-only migration. The .NET API stays in nu/ — this component calls
 * the existing `/api/searches`, `/api/synchronization/*` endpoints cross-origin.
 * The source Page Model's `OnGet` server-rendered the synced list, the logs,
 * and the status dropdown; those were exposed as cookie-authed REST in nu/
 * PR #66 (`/api/synchronization/list`, `/api/synchronization/logs`,
 * `/api/searches/statuses`).
 *
 * Auth: uses the `nu_assist_session` JWT cookie which the nuadvisory
 * `[Authorize(Policy = "NuAssistOrCookie")]` accepts cross-origin. All fetch
 * calls use `credentials: "include"` — no explicit Authorization header.
 *
 * API base URL: VITE_NUADVISORY_API_URL env var.
 *
 * Three tabs:
 *   1. Searches        — status filter + text search + paginated search list
 *                        with per-row selection + Synchronize
 *   2. Synced Searches — stored searches with View-search link + Remove
 *   3. Logs            — latest 50 synchronization log entries
 */
import { useState } from "react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Types — hand-typed from the .NET response shapes
// ---------------------------------------------------------------------------

/** From `ThriveApiSearchModel` (the fields this page reads). */
interface SearchItem {
  id: number;
  attributes: {
    name: string;
    status: string;
    updated_at: string;
    candidacies_count: number;
    client_company: { id: number };
  };
  isSynced: boolean;
}

/** From `ThriveSearchViewModel`. `totalsQty` is the total match count across
 *  all pages (`resultsQty` is just the current page size, == `searches.length`,
 *  so it is not modelled). */
interface ThriveSearchResponse {
  searches: SearchItem[];
  totalsQty?: number;
  nextPageParams?: string | null;
  previousPageParams?: string | null;
}

/** From `UserSynchronization` (GET /api/synchronization/list). `searchId` is
 *  the user-unique key — the .NET DELETE matches on it (the row Id is not
 *  exposed). */
interface SyncedSearch {
  criteria: string | null;
  searchId: number;
  createdOn: string;
}

/** From `UserSynchronizationLog` (GET /api/synchronization/logs). */
interface SyncLog {
  id: number;
  searchId: number;
  createdOn: string;
  count: number | null;
}

/** From `ThriveApiClientService.GetStatusList()` (GET /api/searches/statuses). */
interface StatusOption {
  key: string;
  value: string;
}

type TabId = "searches" | "synced" | "logs";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Sent as `count` to the LEGACY advisory-api (a different endpoint with its
// own bound), not as `page_size` to the v2 API — deliberately NOT
// `listPageSize()` from `@nu-assist/shared-fe`, which sizes the three v2 lists.
const PAGE_SIZE = 100;

// Legacy default status filter (`SelectList(..., "Open")`).
const INITIAL_STATUS = "Open";

// External Thrive deep-link for a search's candidates (legacy "View search").
const THRIVE_SEARCH_BASE = "https://nu.thrivetrm.com/searches";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract the numeric `offset` from a Thrive page-params string. */
function offsetFromParams(params: string | null | undefined): number {
  if (!params) return 0;
  const qIndex = params.indexOf("?");
  const query = qIndex >= 0 ? params.slice(qIndex + 1) : params;
  const value = new URLSearchParams(query).get("offset");
  const parsed = value ? Number.parseInt(value, 10) : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Legacy `moment(updated_at).format('MMMM yyyy')` → e.g. "June 2026". */
function formatMonthYear(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** Legacy `CreatedOn.ToLocalTime().ToString("f")` → long date + short time. */
function formatLogDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

async function fetchSearches(
  searchCriteria: string,
  status: string,
  offset: number,
  fetchImpl: FetchLike = fetch,
): Promise<ThriveSearchResponse> {
  const params = new URLSearchParams({
    count: String(PAGE_SIZE),
    offset: String(offset),
  });
  if (searchCriteria) params.set("searchCriteria", searchCriteria);
  if (status) params.set("status", status);
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/searches?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/searches ${res.status}`);
  return res.json() as Promise<ThriveSearchResponse>;
}

async function fetchSyncedSearches(fetchImpl: FetchLike = fetch): Promise<SyncedSearch[]> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/synchronization/list`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/synchronization/list ${res.status}`);
  return res.json() as Promise<SyncedSearch[]>;
}

async function fetchSyncLogs(fetchImpl: FetchLike = fetch): Promise<SyncLog[]> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/synchronization/logs`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/synchronization/logs ${res.status}`);
  return res.json() as Promise<SyncLog[]>;
}

async function fetchStatuses(fetchImpl: FetchLike = fetch): Promise<StatusOption[]> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/searches/statuses`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/searches/statuses ${res.status}`);
  return res.json() as Promise<StatusOption[]>;
}

async function syncSearches(keys: string[], fetchImpl: FetchLike = fetch): Promise<void> {
  // Ids ride the query string; there is no request body, so no Content-Type.
  const params = new URLSearchParams({ keys: keys.join(",") });
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/synchronization/sync?${params.toString()}`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`POST /api/synchronization/sync ${res.status}`);
}

async function removeSyncedSearch(searchId: number, fetchImpl: FetchLike = fetch): Promise<void> {
  // The .NET DELETE keys on SearchId (not the row Id) — `RemoveAsync(id)`
  // matches `SearchId == id` for the authenticated account.
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/synchronization/${searchId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`DELETE /api/synchronization/${searchId} ${res.status}`);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SyncContacts() {
  const me = useMe();
  const queryClient = useQueryClient();
  const advisoryFetch = useAdvisoryFetch();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>("searches");

  // Searches tab
  const [searchInput, setSearchInput] = useState("");
  const [searchCriteria, setSearchCriteria] = useState("");
  const [status, setStatus] = useState(INITIAL_STATUS);
  const [offset, setOffset] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  const statusesQuery = useQuery({
    queryKey: ["sync", "statuses"],
    queryFn: () => fetchStatuses(advisoryFetch),
    staleTime: 60 * 60 * 1000,
  });

  const searchesQuery = useQuery({
    queryKey: ["sync", "searches", searchCriteria, status, offset],
    queryFn: () => fetchSearches(searchCriteria, status, offset, advisoryFetch),
    staleTime: 30 * 1000,
  });

  const syncedQuery = useQuery({
    queryKey: ["sync", "synced"],
    queryFn: () => fetchSyncedSearches(advisoryFetch),
    staleTime: 30 * 1000,
  });

  const logsQuery = useQuery({
    queryKey: ["sync", "logs"],
    queryFn: () => fetchSyncLogs(advisoryFetch),
    staleTime: 30 * 1000,
  });

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const syncMutation = useMutation({
    mutationFn: (keys: string[]) => syncSearches(keys, advisoryFetch),
    onSuccess: () => {
      setSelectedKeys(new Set());
      toast.success("Your contact synchronization has started — check back in 5–7 minutes.");
      queryClient.invalidateQueries({ queryKey: ["sync", "synced"] });
      queryClient.invalidateQueries({ queryKey: ["sync", "logs"] });
      // Refresh the Searches list so the "Synced" badge reflects the new state.
      queryClient.invalidateQueries({ queryKey: ["sync", "searches"] });
    },
    onError: (err) => {
      toast.error(
        `Error starting synchronization: ${err instanceof Error ? err.message : "unknown error"}`
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: (searchId: number) => removeSyncedSearch(searchId, advisoryFetch),
    onSuccess: () => {
      toast.success("Search has been removed.");
      queryClient.invalidateQueries({ queryKey: ["sync", "synced"] });
      // The removed search may be visible in the Searches list — drop its badge.
      queryClient.invalidateQueries({ queryKey: ["sync", "searches"] });
    },
    onError: (err) => {
      toast.error(`Error removing search: ${err instanceof Error ? err.message : "unknown error"}`);
    },
  });

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const searches = searchesQuery.data?.searches ?? [];
  const synced = syncedQuery.data ?? [];
  const logs = logsQuery.data ?? [];
  const statuses = statusesQuery.data ?? [];

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  // A new search/status defines a different result set, so the cross-page
  // selection no longer maps to visible rows — clear it to avoid a counter
  // that references searches the user can't see. (Selection still persists
  // across pagination WITHIN the same result set.)
  const handleSearch = () => {
    setSearchCriteria(searchInput);
    setOffset(0);
    setSelectedKeys(new Set());
  };

  const handleStatusChange = (next: string) => {
    setStatus(next);
    setOffset(0);
    setSelectedKeys(new Set());
  };

  const toggleKey = (id: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSync = () => {
    if (selectedKeys.size === 0) {
      toast.warning("Select at least one search to synchronize.");
      return;
    }
    syncMutation.mutate([...selectedKeys]);
  };

  // Defensive narrow — WebShellLayout's AuthGuard already wraps this.
  if (me.status !== "authenticated") return null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const tabClass = (id: TabId) =>
    `px-4 py-2 text-body font-medium border-b-2 transition-colors ${
      activeTab === id
        ? "border-accent text-accent"
        : "border-transparent text-text-secondary hover:text-text hover:border-border"
    }`;

  return (
    <div data-testid="sync-contacts" className="p-12">
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-6 text-h1 text-text">Sync Candidate Contact Information</h1>

        {/* Tabs */}
        <div className="mb-6 flex border-b border-border" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "searches"}
            className={tabClass("searches")}
            onClick={() => setActiveTab("searches")}
          >
            Searches
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "synced"}
            className={tabClass("synced")}
            onClick={() => setActiveTab("synced")}
          >
            Synced Searches
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "logs"}
            className={tabClass("logs")}
            onClick={() => setActiveTab("logs")}
          >
            Logs
          </button>
        </div>

        {/* Tab: Searches */}
        {activeTab === "searches" && (
          <div>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <label htmlFor="search-criteria" className="text-label font-medium text-text">
                  Search criteria
                </label>
                <input
                  id="search-criteria"
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  placeholder="Search by name"
                  className="w-64 rounded-md border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="search-status" className="text-label font-medium text-text">
                  Status
                </label>
                <select
                  id="search-status"
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="rounded-sm border border-border bg-surface px-2 py-2 text-body text-text focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {statuses.map((s) => (
                    <option key={s.value} value={s.key}>
                      {s.value}
                    </option>
                  ))}
                </select>
              </div>
              <Button type="button" variant="secondary" onClick={handleSearch}>
                Search
              </Button>

              <div className="ml-auto flex items-center gap-3">
                <span className="text-body text-text">
                  Searches selected: <strong>{selectedKeys.size}</strong>
                </span>
                <Button
                  type="button"
                  onClick={handleSync}
                  disabled={selectedKeys.size === 0 || syncMutation.isPending}
                >
                  Synchronize
                </Button>
              </div>
            </div>

            <p className="mb-4 text-body text-text-secondary">
              Results:{" "}
              <strong className="text-text">
                {searchesQuery.data?.totalsQty ?? searches.length}
              </strong>
            </p>

            {searchesQuery.isLoading && (
              <p className="text-body text-text-muted">Loading searches…</p>
            )}
            {searchesQuery.isError && (
              <p className="text-caption text-error">Error occurred while fetching searches.</p>
            )}

            {!searchesQuery.isLoading && !searchesQuery.isError && (
              <ul className="flex flex-col gap-2">
                {searches.length === 0 ? (
                  <li className="py-6 text-center text-body text-text-muted">No data available</li>
                ) : (
                  searches.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-start gap-4 rounded-md border border-border p-4"
                    >
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(String(s.id))}
                        onChange={(e) => toggleKey(String(s.id), e.target.checked)}
                        className="mt-1 h-4 w-4 accent-accent"
                        aria-label={`Select ${s.attributes.name}`}
                      />
                      <div className="flex-1">
                        <p className="text-body font-semibold text-text">{s.attributes.name}</p>
                        <p className="text-caption text-text-secondary">
                          Status: {s.attributes.status}
                        </p>
                        <p className="text-caption text-text-secondary">
                          Last update: {formatMonthYear(s.attributes.updated_at)}
                        </p>
                        <p className="text-caption text-text-secondary">
                          Candidates: {s.attributes.candidacies_count}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <a
                          href={`${THRIVE_SEARCH_BASE}/${s.id}/candidates`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-caption text-accent hover:text-accent-hover"
                        >
                          View search
                        </a>
                        {s.isSynced && (
                          <span className="text-caption font-semibold text-text">Synced</span>
                        )}
                      </div>
                    </li>
                  ))
                )}
              </ul>
            )}

            {(searchesQuery.data?.previousPageParams || searchesQuery.data?.nextPageParams) && (
              <div className="mt-4 flex justify-center gap-2">
                {searchesQuery.data?.previousPageParams && (
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() =>
                      setOffset(offsetFromParams(searchesQuery.data?.previousPageParams))
                    }
                  >
                    Previous page
                  </Button>
                )}
                {searchesQuery.data?.nextPageParams && (
                  <Button
                    type="button"
                    onClick={() => setOffset(offsetFromParams(searchesQuery.data?.nextPageParams))}
                  >
                    Next page
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tab: Synced Searches */}
        {activeTab === "synced" && (
          <div>
            <h2 className="mb-4 text-h2 text-text">Stored searches</h2>
            {syncedQuery.isLoading && (
              <p className="text-body text-text-muted">Loading stored searches…</p>
            )}
            {syncedQuery.isError && (
              <p className="text-caption text-error">
                Error occurred while fetching stored searches.
              </p>
            )}
            {!syncedQuery.isLoading && !syncedQuery.isError && (
              <ul className="flex flex-col gap-2">
                {synced.length === 0 ? (
                  <li className="py-6 text-center text-body text-text-muted">No stored searches</li>
                ) : (
                  synced.map((item) => (
                    <li
                      key={item.searchId}
                      className="flex items-center justify-between gap-4 rounded-md border border-border p-4"
                    >
                      <span className="text-body text-text">{item.criteria ?? "—"}</span>
                      <div className="flex items-center gap-3">
                        <a
                          href={`${THRIVE_SEARCH_BASE}/${item.searchId}/candidates`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-caption text-accent hover:text-accent-hover"
                        >
                          View search
                        </a>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => removeMutation.mutate(item.searchId)}
                          disabled={
                            removeMutation.isPending && removeMutation.variables === item.searchId
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        )}

        {/* Tab: Logs */}
        {activeTab === "logs" && (
          <div>
            <h2 className="mb-2 text-h2 text-text">Synchronization logs</h2>
            <p className="mb-4 text-caption text-text-secondary">
              Displaying latest 50 synchronizations.
            </p>
            {logsQuery.isLoading && <p className="text-body text-text-muted">Loading logs…</p>}
            {logsQuery.isError && (
              <p className="text-caption text-error">Error occurred while fetching logs.</p>
            )}
            {!logsQuery.isLoading && !logsQuery.isError && (
              <ul className="flex flex-col gap-2">
                {logs.length === 0 ? (
                  <li className="py-6 text-center text-body text-text-muted">No logs yet</li>
                ) : (
                  logs.map((log) => (
                    <li
                      key={log.id}
                      className="rounded-md border border-border p-4 text-body text-text"
                    >
                      Search ID #{log.searchId} on {formatLogDate(log.createdOn)}, {log.count ?? 0}{" "}
                      items processed.
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
